import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {BaseChartDirective} from 'ng2-charts';
import {ChartConfiguration, ChartEvent} from 'chart.js'
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonIcon, IonRow,
    IonSpinner,
} from '@ionic/angular/standalone';
import {ErrorPriority} from '../../../enums/ErrorPriority';
import {ErrorChartData} from '../../../models/dashboard/ErrorChartData';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import * as moment from 'moment';

interface ChartClickedData {
    event?: ChartEvent;
    active?: object[];
}

@Component({
    selector: 'app-dashboard-pivot-chart',
    templateUrl: './dashboard-pivot-chart.component.html',
    styleUrls: ['./dashboard-pivot-chart.component.css'],
    standalone: true,
    imports: [
        BaseChartDirective,
        IonCard,
        IonCardContent,
        IonCardHeader,
        IonCardTitle,
        IonButton,
        IonIcon,
        TranslatePipe,
        IonSpinner,
        IonRow,
    ]
})
export class DashboardPivotChartComponent implements OnInit, OnDestroy {
    private readonly errorTypeConfig: Map<ErrorPriority, { labelKey: string; color: string }> =
        new Map<ErrorPriority, { labelKey: string; color: string }>([
            ['error', { labelKey: 'error.type.error', color: 'rgba(197, 0, 15, 0.65)' }],
            ['warning', { labelKey: 'error.type.warning', color: 'rgba(255, 196, 9, 0.65)' }],
            ['information', { labelKey: 'error.type.information', color: 'rgba(0, 84, 233, 0.65)' }],
        ]);

    private langChangeSubscription?: Subscription;

    /** Maps lowercase Dutch month names to their 0-based month index. */
    private readonly dutchMonthIndex: Map<string, number> = new Map([
        ['januari', 0], ['februari', 1], ['maart', 2], ['april', 3],
        ['mei', 4], ['juni', 5], ['juli', 6], ['augustus', 7],
        ['september', 8], ['oktober', 9], ['november', 10], ['december', 11],
    ]);

    /** Maps lowercase Dutch day names to their 0-based ISO weekday index (Mon=0 … Sun=6). */
    private readonly dutchDayIndex: Map<string, number> = new Map([
        ['maandag', 0], ['dinsdag', 1], ['woensdag', 2], ['donderdag', 3],
        ['vrijdag', 4], ['zaterdag', 5], ['zondag', 6],
    ]);

    /** Matches Dutch week labels from the API, e.g. "Week 3". */
    private readonly weekPattern = /^Week (\d+)$/i;

    /**
     * Converts a Dutch label from the API to the equivalent in the currently active locale:
     * - Month names  (month level)
     * - "Week N"     (week level)  → translated week word + number
     * - Day names    (day level)
     * Anything else (plain numbers, dates) is returned unchanged.
     */
    private localizeDisplayValue(displayValue: string): string {
        // Month names
        const monthIndex = this.dutchMonthIndex.get(displayValue.toLowerCase());
        if (monthIndex !== undefined) {
            return moment.localeData().months()[monthIndex];
        }

        // "Week N" labels
        const weekMatch = this.weekPattern.exec(displayValue);
        if (weekMatch) {
            const weekWord = this.translateService.instant('components.dashboard.chart.weekLabel');
            return `${weekWord} ${weekMatch[1]}`;
        }

        // Day names
        const dayIndex = this.dutchDayIndex.get(displayValue.toLowerCase());
        if (dayIndex !== undefined) {
            // moment.localeData().weekdays() = [Sunday, Monday, …, Saturday]
            // dutchDayIndex: 0=maandag(Mon) … 6=zondag(Sun) → shift +1, wrap Sunday
            return moment.localeData().weekdays()[(dayIndex + 1) % 7];
        }

        return displayValue;
    }

    public barChartData?: ChartConfiguration<'bar'>['data'];
    public readonly barChartOptions: ChartConfiguration<'bar'>['options'] = {
        plugins: {
            legend: {
                display: true,
                align: "end"
            },
        },
        responsive: true,
        scales: {
            x: {
                grid: {
                    display: false,
                }
            },
            y: {
                ticks: {
                    callback: (value) => {
                        return Number.isInteger(value) ? value : '';
                    }
                }
            }
        }
    };

    @Input({ required: true }) title!: string;
    @Input({ required: true }) data?: ErrorChartData;
    @Input() canZoomOut: boolean = false;
    @Input() isLoading: boolean = false;

    @Output() zoomIn: EventEmitter<number> = new EventEmitter<number>();
    @Output() zoomOut: EventEmitter<void> = new EventEmitter<void>();

    public chartClicked(data: ChartClickedData) {
        if (data.active != undefined && !this.isLoading) {
            const clickedBarData: any = data.active[0];
            const currentIndex: number = clickedBarData.index;

            const label = this.data?.labels[currentIndex];
            if (label) {
                const newValue = label.value;
                this.zoomIn.emit(newValue);
            }
        }
    }

    constructor(private readonly translateService: TranslateService) {
    }

    public ngOnInit(): void {
        this.updateChart();
        this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
            this.updateChart();
        });
    }

    public ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            this.updateChart();
        }
    }

    private updateChart(): void {
        if (this.data) {
            this.barChartData = {
                labels: this.data.labels.map(label => this.localizeDisplayValue(label.displayValue)),
                datasets: this.data.datasets.map(dataset => {
                    const datasetType: ErrorPriority = dataset.type;
                    const errorTypeConfig = this.errorTypeConfig.get(datasetType);
                    return {
                        data: dataset.data,
                        label: errorTypeConfig ? this.translateService.instant(errorTypeConfig.labelKey) : undefined,
                        backgroundColor: errorTypeConfig?.color
                    };
                })
            };
        }
    }

    protected onZoomOut() {
        this.zoomOut.emit();
    }
}
